import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Heading,
  VStack,
  Alert,
  AlertIcon,
  ButtonGroup,
  useToast,
  Checkbox,
  SimpleGrid,
} from '@chakra-ui/react';
import { patientApi, goalsApi, PatientsService } from '../services/api';

function PatientForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const toast = useToast();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    phone: '',
    address: '',
    allergies: '',
    current_medications: '',
    medical_conditions: '',
    previous_surgeries: '',
    previous_cosmetic_procedures: '',
    medical_notes: '',
    other_goals: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableGoals, setAvailableGoals] = useState([]);
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);

  useEffect(() => {
    fetchGoals();
    if (isEdit) {
      fetchPatient();
      fetchPatientGoals();
    }
    // eslint-disable-next-line
  }, [id]);

  const fetchGoals = async () => {
    try {
      const response = await goalsApi.getAll(false); // Only get active goals
      setAvailableGoals(response.data);
    } catch (err) {
      console.error('Failed to fetch goals:', err);
    }
  };

  const fetchPatient = async () => {
    try {
      const response = await patientApi.getById(id);
      setFormData(response.data);
    } catch (err) {
      setError('Failed to fetch patient');
      console.error(err);
    }
  };

  const fetchPatientGoals = async () => {
    try {
      const response = await PatientsService.getPatientGoalsApiPatientsPatientIdGoalsGet({ patientId: id });
      setSelectedGoalIds(response.map(goal => goal.id));
    } catch (err) {
      console.error('Failed to fetch patient goals:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoalToggle = (goalId) => {
    setSelectedGoalIds((prev) => {
      if (prev.includes(goalId)) {
        return prev.filter(id => id !== goalId);
      } else {
        return [...prev, goalId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let patientId = id;

      if (isEdit) {
        await patientApi.update(id, formData);
      } else {
        const response = await patientApi.create(formData);
        patientId = response.data.id;
      }

      // Save patient goals
      await PatientsService.updatePatientGoalsApiPatientsPatientIdGoalsPut({
        patientId: patientId,
        requestBody: { goal_ids: selectedGoalIds }
      });

      toast({
        title: isEdit ? 'Patient updated successfully' : 'Patient created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/app/patients');
    } catch (err) {
      setError(isEdit ? 'Failed to update patient' : 'Failed to create patient');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>
        {isEdit ? 'Edit Patient' : 'Add New Patient'}
      </Heading>

      {error && (
        <Alert status="error" borderRadius="lg" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Box as="form" onSubmit={handleSubmit} bg="white" p={8} borderRadius="xl" boxShadow="sm" maxW="2xl">
        <VStack spacing={5} align="stretch">
          <FormControl isRequired>
            <FormLabel>First Name</FormLabel>
            <Input
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Enter first name"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Last Name</FormLabel>
            <Input
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Enter last name"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Date of Birth</FormLabel>
            <Input
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="patient@example.com"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Phone</FormLabel>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Address</FormLabel>
            <Textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
              placeholder="Enter full address"
            />
          </FormControl>

          {/* Client Goals Section */}
          <Heading size="md" pt={4} pb={2} color="purple.700">
            Client Goals
          </Heading>

          <FormControl>
            <FormLabel>What are your aesthetic goals? (Select all that apply)</FormLabel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={2}>
              {availableGoals.map((goal) => (
                <Checkbox
                  key={goal.id}
                  isChecked={selectedGoalIds.includes(goal.id)}
                  onChange={() => handleGoalToggle(goal.id)}
                  colorScheme="purple"
                >
                  {goal.name}
                </Checkbox>
              ))}
            </SimpleGrid>
          </FormControl>

          <FormControl>
            <FormLabel>Other Goals</FormLabel>
            <Textarea
              name="other_goals"
              value={formData.other_goals}
              onChange={handleChange}
              rows={2}
              placeholder="Please specify any additional goals not listed above"
            />
          </FormControl>

          {/* Medical History Section */}
          <Heading size="md" pt={4} pb={2} color="purple.700">
            Medical History
          </Heading>

          <FormControl>
            <FormLabel>Allergies</FormLabel>
            <Textarea
              name="allergies"
              value={formData.allergies}
              onChange={handleChange}
              rows={2}
              placeholder="Medications, products, or substances the patient is allergic to"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Current Medications</FormLabel>
            <Textarea
              name="current_medications"
              value={formData.current_medications}
              onChange={handleChange}
              rows={2}
              placeholder="Current medications and supplements"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Medical Conditions</FormLabel>
            <Textarea
              name="medical_conditions"
              value={formData.medical_conditions}
              onChange={handleChange}
              rows={2}
              placeholder="Chronic diseases or medical conditions"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Previous Surgeries</FormLabel>
            <Textarea
              name="previous_surgeries"
              value={formData.previous_surgeries}
              onChange={handleChange}
              rows={2}
              placeholder="Surgical history"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Previous Cosmetic Procedures</FormLabel>
            <Textarea
              name="previous_cosmetic_procedures"
              value={formData.previous_cosmetic_procedures}
              onChange={handleChange}
              rows={2}
              placeholder="Previous aesthetic treatments (Botox, fillers, etc.)"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Medical Notes</FormLabel>
            <Textarea
              name="medical_notes"
              value={formData.medical_notes}
              onChange={handleChange}
              rows={3}
              placeholder="Additional medical notes or relevant information"
            />
          </FormControl>

          <ButtonGroup spacing={4} pt={4}>
            <Button type="submit" colorScheme="purple" isLoading={loading} loadingText="Saving">
              {isEdit ? 'Update Patient' : 'Create Patient'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/app/patients')}>
              Cancel
            </Button>
          </ButtonGroup>
        </VStack>
      </Box>
    </Box>
  );
}

export default PatientForm;
